import React, { PureComponent } from 'react';
import { Button, Dropdown, Form, Input, List } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { CloseCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { InitialNetworkPlanDto, RegulatedInfo, RegulatedInfo_RegulatedInfoType } from 'app/model/SOL49';
import ConditionalRender from 'app/common/components/ConditionalRender';
import Utils from 'app/common/util/Utils';

interface NetworkSummaryPanelProps {
    inp: InitialNetworkPlanDto;
    onChange: (inp: InitialNetworkPlanDto) => void;
}

export class NetworkSummaryPanel extends PureComponent<NetworkSummaryPanelProps> {
    render() {
        const aerodromes = this.selectedOnly(this.props.inp.networkSummary.aerodromes);
        const airspace = this.selectedOnly(this.props.inp.networkSummary.airspace);

        const aerodromeMeasureItems = this.getMenuItems(
            this.props.inp.networkSummary.aerodromes,
            RegulatedInfo_RegulatedInfoType.MEASURE,
            ad => this.addAerodrome(ad),
        );
        const airspaceMeasureItems = this.getMenuItems(
            this.props.inp.networkSummary.airspace,
            RegulatedInfo_RegulatedInfoType.MEASURE,
            as => this.addAirspace(as),
        );
        const aerodromeHotspotItems = this.getMenuItems(
            this.props.inp.networkSummary.aerodromes,
            RegulatedInfo_RegulatedInfoType.HOTSPOT,
            ad => this.addAerodrome(ad),
        );
        const airspaceHotspotItems = this.getMenuItems(
            this.props.inp.networkSummary.airspace,
            RegulatedInfo_RegulatedInfoType.HOTSPOT,
            as => this.addAirspace(as),
        );

        return (
            <Form layout="vertical" style={{ padding: '1em' }}>
                <Form.Item label="General">
                    <div style={{ paddingLeft: '0.5em' }}>
                        <p>{this.props.inp.networkSummary.expectedFlightInfo}</p>
                        <TextArea
                            rows={10}
                            value={this.props.inp.networkSummary.generalInfo}
                            onChange={e => this.updateGeneralText(e.target.value)}
                        />
                    </div>
                </Form.Item>
                <Form.Item
                    label={
                        <div>
                            <span style={{ width: '5em', display: 'inline-flex' }}>Aerodrome</span>
                            <ConditionalRender condition={Utils.isNotEmpty(aerodromeMeasureItems)}>
                                <Dropdown
                                    menu={{ items: aerodromeMeasureItems }}
                                    trigger={['click']}
                                    overlayStyle={{ maxHeight: '25em', overflow: 'auto', borderRadius: '0.5em' }}>
                                    <Button style={{ marginLeft: '2em' }}>
                                        <span>
                                            Add from measure <PlusCircleOutlined style={{ paddingLeft: '1em' }} />
                                        </span>
                                    </Button>
                                </Dropdown>
                            </ConditionalRender>
                            <ConditionalRender condition={Utils.isNotEmpty(aerodromeHotspotItems)}>
                                <Dropdown
                                    menu={{ items: aerodromeHotspotItems }}
                                    trigger={['click']}
                                    overlayStyle={{ maxHeight: '25em', overflow: 'auto', borderRadius: '0.5em' }}>
                                    <Button style={{ marginLeft: '2em' }}>
                                        <span>
                                            Add from hotspot <PlusCircleOutlined style={{ paddingLeft: '1em' }} />
                                        </span>
                                    </Button>
                                </Dropdown>
                            </ConditionalRender>
                            <Button style={{ marginLeft: '2em' }} onClick={() => this.addAerodromeFreeText()}>
                                <span>
                                    Add free text <PlusCircleOutlined style={{ paddingLeft: '1em' }} />
                                </span>
                            </Button>
                        </div>
                    }>
                    <>
                        {Object.keys(aerodromes).map((ad, i) => (
                            <List.Item
                                key={i}
                                extra={
                                    <CloseCircleOutlined
                                        onClick={() => this.deleteAerodrome(ad)}
                                        className="show-on-hover"
                                        style={{ paddingLeft: '0.5em' }}
                                    />
                                }>
                                <Input
                                    style={{ width: '8em', marginRight: '2em', fontWeight: 'bold' }}
                                    value={aerodromes[ad].location}
                                    onChange={({ target }) => this.updateAerodrome(ad, target.value)}
                                />
                                <Input
                                    value={aerodromes[ad].info}
                                    onChange={({ target }) => this.updateAerodromeText(ad, target.value)}
                                />
                            </List.Item>
                        ))}
                        {this.props.inp.networkSummary.aerodromesFreeText.map((text, idx) => (
                            <List.Item
                                key={idx}
                                extra={
                                    <CloseCircleOutlined
                                        onClick={() => this.deleteAerodromeFreeText(idx)}
                                        className="show-on-hover"
                                        style={{ paddingLeft: '0.5em' }}
                                    />
                                }>
                                <TextArea
                                    value={text}
                                    onChange={({ target }) => this.updateAerodromeFreeText(idx, target.value)}
                                />
                            </List.Item>
                        ))}
                    </>
                </Form.Item>
                <Form.Item
                    label={
                        <div>
                            <span style={{ width: '5em', display: 'inline-flex' }}>Airspace</span>
                            <ConditionalRender condition={Utils.isNotEmpty(airspaceMeasureItems)}>
                                <Dropdown
                                    menu={{ items: airspaceMeasureItems }}
                                    trigger={['click']}
                                    overlayStyle={{ maxHeight: '25em', overflow: 'auto', borderRadius: '0.5em' }}>
                                    <Button style={{ marginLeft: '2em' }}>
                                        <span>
                                            Add from measure <PlusCircleOutlined style={{ paddingLeft: '1em' }} />
                                        </span>
                                    </Button>
                                </Dropdown>
                            </ConditionalRender>
                            <ConditionalRender condition={Utils.isNotEmpty(airspaceHotspotItems)}>
                                <Dropdown
                                    menu={{ items: airspaceHotspotItems }}
                                    trigger={['click']}
                                    overlayStyle={{ maxHeight: '25em', overflow: 'auto', borderRadius: '0.5em' }}>
                                    <Button style={{ marginLeft: '2em' }}>
                                        <span>
                                            Add from hotspot <PlusCircleOutlined style={{ paddingLeft: '1em' }} />
                                        </span>
                                    </Button>
                                </Dropdown>
                            </ConditionalRender>
                            <Button style={{ marginLeft: '2em' }} onClick={() => this.addAirspaceFreeText()}>
                                <span>
                                    Add free text <PlusCircleOutlined style={{ paddingLeft: '1em' }} />
                                </span>
                            </Button>
                        </div>
                    }>
                    <>
                        {Object.keys(airspace).map((as, i) => (
                            <List.Item
                                key={i}
                                extra={
                                    <CloseCircleOutlined
                                        onClick={() => this.deleteAirspace(as)}
                                        className="show-on-hover"
                                        style={{ paddingLeft: '0.5em' }}
                                    />
                                }>
                                <Input
                                    style={{ width: '8em', marginRight: '2em', fontWeight: 'bold' }}
                                    value={airspace[as].location}
                                    onChange={({ target }) => this.updateAirspace(as, target.value)}
                                />
                                <Input
                                    value={airspace[as].info}
                                    onChange={({ target }) => this.updateAirspaceText(as, target.value)}
                                />
                            </List.Item>
                        ))}
                        {this.props.inp.networkSummary.airspaceFreeText.map((text, idx) => (
                            <List.Item
                                key={idx}
                                extra={
                                    <CloseCircleOutlined
                                        onClick={() => this.deleteAirspaceFreeText(idx)}
                                        className="show-on-hover"
                                        style={{ paddingLeft: '0.5em' }}
                                    />
                                }>
                                <TextArea
                                    value={text}
                                    onChange={({ target }) => this.updateAirspaceFreeText(idx, target.value)}
                                />
                            </List.Item>
                        ))}
                    </>
                </Form.Item>
                <Form.Item label="Weather">
                    <TextArea
                        rows={5}
                        value={this.props.inp.networkSummary.weather}
                        onChange={({ target }) => this.updateWeatherInfo(target.value)}
                    />
                </Form.Item>
            </Form>
        );
    }

    private selectedOnly = (regulatedInfos: { [p: string]: RegulatedInfo }): { [p: string]: RegulatedInfo } => {
        return Object.fromEntries(Object.entries(regulatedInfos).filter(([_, info]) => info.selected));
    };

    private getMenuItems = (
        infos: {
            [p: string]: RegulatedInfo;
        },
        type: RegulatedInfo_RegulatedInfoType,
        onClick: (s: string) => void,
    ) => {
        return Object.entries(infos)
            .filter(([_, info]) => info.type === type && !info.selected)
            .map(([ad, info], idx) => ({
                key: idx,
                label: <div onClick={() => onClick(ad)}>{info.location}</div>,
            }));
    };

    private updateGeneralText = (text: string) => {
        this.props.onChange({
            ...this.props.inp,
            networkSummary: { ...this.props.inp.networkSummary, generalInfo: text },
        });
    };

    private addAerodromeFreeText = () => {
        const newList = this.props.inp.networkSummary.aerodromesFreeText.concat('');
        this.props.onChange({
            ...this.props.inp,
            networkSummary: { ...this.props.inp.networkSummary, aerodromesFreeText: newList },
        });
    };
    private deleteAerodromeFreeText = (idx: number) => {
        const newList = this.props.inp.networkSummary.aerodromesFreeText.filter((_, i) => i !== idx);
        this.props.onChange({
            ...this.props.inp,
            networkSummary: { ...this.props.inp.networkSummary, aerodromesFreeText: newList },
        });
    };

    private updateAerodromeFreeText = (idx: number, text: string) => {
        const newList = this.props.inp.networkSummary.aerodromesFreeText.map((t, i) => (i !== idx ? t : text));
        this.props.onChange({
            ...this.props.inp,
            networkSummary: { ...this.props.inp.networkSummary, aerodromesFreeText: newList },
        });
    };

    private addAerodrome = (ad: string) => {
        const copy = Object.assign({}, this.props.inp.networkSummary.aerodromes);
        copy[ad].selected = true;
        this.props.onChange({
            ...this.props.inp,
            networkSummary: { ...this.props.inp.networkSummary, aerodromes: copy },
        });
    };

    private deleteAerodrome = (ad: string) => {
        const copy = Object.assign({}, this.props.inp.networkSummary.aerodromes);
        copy[ad].selected = false;

        this.props.onChange({
            ...this.props.inp,
            networkSummary: { ...this.props.inp.networkSummary, aerodromes: copy },
        });
    };

    private updateAerodromeText = (ad: string, info: string) => {
        const copy = Object.assign({}, this.props.inp.networkSummary.aerodromes);
        copy[ad].info = info;
        this.props.onChange({
            ...this.props.inp,
            networkSummary: { ...this.props.inp.networkSummary, aerodromes: copy },
        });
    };

    private updateAerodrome = (ad: string, location: string) => {
        const copy = Object.assign({}, this.props.inp.networkSummary.aerodromes);
        copy[ad].location = location;
        this.props.onChange({
            ...this.props.inp,
            networkSummary: { ...this.props.inp.networkSummary, aerodromes: copy },
        });
    };

    private addAirspaceFreeText = () => {
        const newList = this.props.inp.networkSummary.airspaceFreeText.concat('');
        this.props.onChange({
            ...this.props.inp,
            networkSummary: { ...this.props.inp.networkSummary, airspaceFreeText: newList },
        });
    };

    private deleteAirspaceFreeText = (idx: number) => {
        const newList = this.props.inp.networkSummary.airspaceFreeText.filter((_, i) => i !== idx);
        this.props.onChange({
            ...this.props.inp,
            networkSummary: { ...this.props.inp.networkSummary, airspaceFreeText: newList },
        });
    };

    private updateAirspaceFreeText = (idx: number, text: string) => {
        const newList = this.props.inp.networkSummary.airspaceFreeText.map((t, i) => (i !== idx ? t : text));
        this.props.onChange({
            ...this.props.inp,
            networkSummary: { ...this.props.inp.networkSummary, airspaceFreeText: newList },
        });
    };

    private addAirspace = (ad: string) => {
        const copy = Object.assign({}, this.props.inp.networkSummary.airspace);
        copy[ad].selected = true;

        this.props.onChange({
            ...this.props.inp,
            networkSummary: { ...this.props.inp.networkSummary, airspace: copy },
        });
    };

    private deleteAirspace = (as: string) => {
        const copy = Object.assign({}, this.props.inp.networkSummary.airspace);
        copy[as].selected = false;

        this.props.onChange({
            ...this.props.inp,
            networkSummary: { ...this.props.inp.networkSummary, airspace: copy },
        });
    };

    private updateAirspace = (as: string, location: string) => {
        const copy = Object.assign({}, this.props.inp.networkSummary.airspace);
        copy[as].location = location;
        this.props.onChange({
            ...this.props.inp,
            networkSummary: { ...this.props.inp.networkSummary, airspace: copy },
        });
    };

    private updateAirspaceText = (as: string, info: string) => {
        const copy = Object.assign({}, this.props.inp.networkSummary.airspace);
        copy[as].info = info;
        this.props.onChange({
            ...this.props.inp,
            networkSummary: { ...this.props.inp.networkSummary, airspace: copy },
        });
    };

    private updateWeatherInfo(text: string) {
        this.props.onChange({
            ...this.props.inp,
            networkSummary: { ...this.props.inp.networkSummary, weather: text },
        });
    }
}
